import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import {
  PET_CLAIM_ACCEPTED_FILE_TYPES,
  PET_CLAIM_PICTURE_TYPES,
} from '@/shared/constants/claims'
import {
  screenSelectedFiles,
  validateInvoiceFiles,
  validatePetPictureFile,
} from '@/shared/helpers/claim-files'
import type { PetClaimFormValues } from '../page.claims.pet.hook'

export function useClaimsPetStepTwo() {
  const { t } = useTranslation()
  const {
    control,
    setValue,
    getValues,
    setError,
    watch,
    formState: { errors },
  } = useFormContext<PetClaimFormValues>()

  const rules = {
    receipts: {
      validate: (files: File[]) =>
        validateInvoiceFiles(t, files, files.length, [getValues('petPicture')]),
    },
    petPicture: {
      validate: (file: File | undefined) => validatePetPictureFile(t, file),
    },
    signature: { required: t('common.required') },
  }

  const fieldErrors = {
    receipts: errors.receipts?.message,
    petPicture: errors.petPicture?.message,
    signature: errors.signature?.message,
  }

  function addReceipts(files: File[]) {
    const { accepted, rejectionMessage } = screenSelectedFiles(
      t,
      files,
      PET_CLAIM_ACCEPTED_FILE_TYPES,
    )
    setValue('receipts', [...getValues('receipts'), ...accepted], {
      shouldDirty: true,
      shouldValidate: true,
    })
    // Set after validating, or the passing validation would clear it
    if (rejectionMessage) setError('receipts', { message: rejectionMessage })
  }

  function removeReceipt(index: number) {
    setValue(
      'receipts',
      getValues('receipts').filter((_, fileIndex) => fileIndex !== index),
      { shouldDirty: true, shouldValidate: true },
    )
  }

  function selectPetPicture(file: File) {
    const { accepted, rejectionMessage } = screenSelectedFiles(
      t,
      [file],
      PET_CLAIM_PICTURE_TYPES,
    )
    if (rejectionMessage || !accepted.length) {
      setError('petPicture', { message: rejectionMessage })
      return
    }
    setValue('petPicture', file, { shouldDirty: true, shouldValidate: true })
  }

  const petPicture = watch('petPicture')
  const petPicturePreviewUrl = useMemo(
    () => (petPicture ? URL.createObjectURL(petPicture) : undefined),
    [petPicture],
  )
  useEffect(() => {
    return () => {
      if (petPicturePreviewUrl) URL.revokeObjectURL(petPicturePreviewUrl)
    }
  }, [petPicturePreviewUrl])

  return {
    control,
    rules,
    fieldErrors,
    addReceipts,
    removeReceipt,
    selectPetPicture,
    petPicturePreviewUrl,
  }
}
